/**
 * «Μαζική αλλαγή» για τα τσεκαρισμένα προϊόντα: διαθεσιμότητα, ταμείο, κατηγορία, χρώμα, ομάδες
 * επιλογών. Όλα μπαίνουν στο πρόχειρο και γράφονται με την ίδια «Αποθήκευση».
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Eye, EyeOff, FolderInput, Info, Loader2, Plus, X } from 'lucide-react';
import CatalogColorPicker from '../shared/CatalogColorPicker';
import { CatRef, GroupRef } from './catalogModel';
import type { ProductView } from './catalogView';
import type { GroupInfo } from './ProductOptionsTab';
import type { CategoryOption } from './ProductBasicsTab';
import { cx, MenuButton, ToneDot } from './ui';

export interface BulkPanelProps {
  views: ProductView[];
  categoryOptions: CategoryOption[];
  groups: GroupInfo[];
  /** οι συνδέσεις όλων των επιλεγμένων φορτώθηκαν */
  groupsLoaded: boolean;
  readOnly: boolean;
  floating: boolean;
  /** κρατικά τέλη ανάμεσα στα επιλεγμένα: μένουν εκτός μαζικής απόκρυψης / «μη διαθέσιμα» */
  feeNames: string[];
  onAvailable: (value: boolean) => void;
  onHidden: (value: boolean) => void;
  onMove: (ref: CatRef) => void;
  onColor: (tone: string | null) => void;
  onGroupAll: (ref: GroupRef) => void;
  onGroupRemove: (ref: GroupRef) => void;
  onClear: () => void;
}

const BulkPanel: React.FC<BulkPanelProps> = props => {
  const { t } = useTranslation(['catalog']);
  const { views, readOnly } = props;
  const count: Record<string, number> = {};
  views.forEach(v => (v.groups || []).forEach(g => { count[String(g)] = (count[String(g)] || 0) + 1; }));
  const byRef = new Map(props.groups.map(g => [String(g.ref), g] as [string, GroupInfo]));
  const tones = Array.from(new Set(views.map(v => v.color || '')));
  const commonTone = tones.length === 1 ? (tones[0] || null) : undefined;
  const allAvailable = views.every(v => v.is_available);
  const allUnavailable = views.every(v => !v.is_available);
  const allHidden = views.every(v => v.is_hidden);
  const allVisible = views.every(v => !v.is_hidden);

  return (
    <aside className={cx('cx-pane cx-panel', props.floating && 'floating')} aria-label={t('catalog:bulk.title')}>
      <div className="cx-panel-head">
        <div className="cx-ph-text">
          <div className="cx-crumb">{t('catalog:bulk.title')}</div>
          <h2><span className="cx-title-text">{t('catalog:bulk.count', { count: views.length })}</span></h2>
        </div>
        <button type="button" className="cx-icon-btn" onClick={props.onClear} aria-label={t('catalog:list.clearSelection')} title={t('catalog:list.clearSelection')}>
          <X className="cx-ico" />
        </button>
      </div>
      <div className="cx-panel-body">
        <fieldset disabled={readOnly}>
          <div className="cx-chips">
            {views.map(v => <span key={v.key} className="cx-chip">{v.color && <ToneDot tone={v.color} />}{v.name || t('catalog:product.untitled')}</span>)}
          </div>
          {props.feeNames.length > 0 && (
            <div className="cx-note" role="note" data-testid="bulk-fee-note">
              <Info className="cx-ico" />
              <span><b>{t('catalog:fees.bulkExcludedTitle', { names: props.feeNames.join(', ') })}</b> {t('catalog:fees.explanation')}</span>
            </div>
          )}
          <div className="cx-bsec">
            <div className="cx-bt">{t('catalog:fields.availability')}</div>
            <div className="cx-btn-row">
              <button type="button" className="cx-btn sm" aria-pressed={allAvailable} onClick={() => props.onAvailable(true)}><Check className="cx-ico" /> {t('catalog:bulk.available')}</button>
              <button type="button" className="cx-btn sm" aria-pressed={allUnavailable} onClick={() => props.onAvailable(false)}>{t('catalog:bulk.unavailable')}</button>
            </div>
          </div>
          <div className="cx-bsec">
            <div className="cx-bt">{t('catalog:fields.onPos')}</div>
            <div className="cx-btn-row">
              <button type="button" className="cx-btn sm" aria-pressed={allVisible} onClick={() => props.onHidden(false)}><Eye className="cx-ico" /> {t('catalog:bulk.visible')}</button>
              <button type="button" className="cx-btn sm" aria-pressed={allHidden} onClick={() => props.onHidden(true)}><EyeOff className="cx-ico" /> {t('catalog:bulk.hidden')}</button>
            </div>
          </div>
          <div className="cx-bsec">
            <label className="cx-bt" htmlFor="bulk-move">{t('catalog:fields.category')}</label>
            <div className="cx-search">
              <FolderInput className="cx-ico" aria-hidden="true" />
              <select id="bulk-move" className="cx-inp" value="" onChange={e => {
                const raw = e.target.value;
                if (raw === '') return;
                props.onMove(/^\d+$/.test(raw) ? Number(raw) : raw);
              }}>
                <option value="">{t('catalog:bulk.moveTo')}</option>
                {props.categoryOptions.map(o => <option key={String(o.ref)} value={String(o.ref)}>{o.label}</option>)}
              </select>
            </div>
            <p className="cx-hint">{t('catalog:bulk.moveHint')}</p>
          </div>
          <div className="cx-bsec">
            <CatalogColorPicker kind="product" idPrefix="bulk-color" value={commonTone === undefined ? undefined : commonTone} compactLabel onChange={tone => props.onColor(tone)} />
          </div>
          <div className="cx-bsec">
            <div className="cx-bt">{t('catalog:fields.optionGroups')}</div>
            {!props.groupsLoaded ? (
              <span className="cx-hint" role="status"><Loader2 className="cx-ico animate-spin" style={{ display: 'inline' }} /> {t('catalog:common.loading')}</span>
            ) : (
              <>
                {Object.keys(count).map(ref => {
                  const g = byRef.get(ref);
                  const n = count[ref];
                  const groupRef: GroupRef = /^\d+$/.test(ref) ? Number(ref) : ref;
                  return (
                    <div key={ref} className="cx-ogm">
                      <b>{g?.name || `#${ref}`}</b>
                      <span className="cx-hint">{n === views.length ? t('catalog:bulk.inAll', { count: n }) : t('catalog:bulk.inSome', { count: n, total: views.length })}</span>
                      <span className="cx-sp" />
                      {n < views.length && <button type="button" className="cx-btn ghost xs" onClick={() => props.onGroupAll(groupRef)}>{t('catalog:bulk.toAll')}</button>}
                      <button type="button" className="cx-btn ghost xs danger" onClick={() => props.onGroupRemove(groupRef)}>{t('catalog:bulk.remove')}</button>
                    </div>
                  );
                })}
                <div className="cx-btn-row">
                  <MenuButton
                    label={t('catalog:bulk.addGroupAll')}
                    className="cx-btn sm"
                    disabled={readOnly || props.groups.length === 0}
                    items={props.groups.filter(g => (count[String(g.ref)] || 0) < views.length).map(g => ({
                      key: String(g.ref), label: g.name, onSelect: () => props.onGroupAll(g.ref),
                    }))}
                  >
                    <Plus className="cx-ico" /> {t('catalog:bulk.addGroupAll')} <ChevronDown className="cx-ico" />
                  </MenuButton>
                </div>
              </>
            )}
          </div>
          <div className="cx-note"><Info className="cx-ico" /><span>{t('catalog:bulk.note')}</span></div>
        </fieldset>
      </div>
    </aside>
  );
};

export default BulkPanel;
